/**
 * Deep-link fragment scroll — honour the URL's #hash on first load.
 *
 * Native "scroll to #hash on load" is unreliable here: <html> carries
 * scroll-behavior: smooth and Swup + async-Alpine islands reflow the viewport
 * just after first paint, so the browser's on-load fragment jump lands at the
 * top. Reported on /knock-down-rebuild/...#popular-designs (MOHO-40). (The
 * separate scroll FREEZE — overflow-x:clip on <html> — is fixed in _reset.css;
 * this handles the timing so the jump actually fires.)
 *
 * Swup's `page:view` hook fires once the page is fully set up — on the initial
 * load AND every visit — after Swup's own scroll handling, which is the reliable
 * moment to apply the jump. A short retry backs it up in case layout is still
 * settling, and it bails the instant the user scrolls so it never fights them.
 *
 * Same-page anchor CLICKS already work (browser handles them live; Swup ignores
 * pure #links). scrollIntoView honours scroll-margin-top so the section lands
 * below the sticky header + anchor bar; behavior:'auto' = instant jump.
 *
 * @param {import('swup').default} [swup] the running router instance
 */
export function initHashScroll(swup) {
    const scrollToHash = () => {
        const hash = window.location.hash;
        if (!hash || hash === '#swup') {
            return;
        }
        let id;
        try {
            id = decodeURIComponent(hash.slice(1));
        } catch {
            id = hash.slice(1);
        }
        const target = document.getElementById(id);
        if (target) {
            target.scrollIntoView({ behavior: 'auto' });
        }
    };

    // Primary: fire after Swup finishes setting up the page (past its scroll
    // handling). No-ops on hashless navigations.
    swup?.hooks?.on('page:view', scrollToHash);

    // Backup for the first load: retry across the settling window, bailing the
    // instant the user takes over so a retry never yanks them. Only genuine
    // input counts — programmatic scrollIntoView fires none of these.
    if (!window.location.hash || window.location.hash === '#swup') {
        return;
    }
    let userTookOver = false;
    const yield_ = () => {
        userTookOver = true;
    };
    const opts = { passive: true, once: true };
    ['wheel', 'touchmove', 'keydown'].forEach((evt) => window.addEventListener(evt, yield_, opts));

    let attempts = 0;
    const retry = () => {
        if (userTookOver) {
            return;
        }
        scrollToHash();
        if (attempts++ < 30) {
            setTimeout(retry, 80);
        }
    };
    requestAnimationFrame(retry);
}
